/**
 * Capsule 3 — Journey Builder (User Dashboard Tab).
 * Lists user's journeys + lets them create a new one with a step-picker.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Compass, Plus, Trash2, Check, ChevronRight, X, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_TYPES = [
  { id: "capscan", label: "CapScan", hint: "Run a capability scan" },
  { id: "coaching", label: "Coaching", hint: "1-on-1 AI coaching session" },
  { id: "assessment", label: "Assessment", hint: "Take a skill assessment" },
  { id: "axis", label: "AXIS", hint: "Behavioural style snapshot" },
  { id: "milestone", label: "Milestone", hint: "Self-set checkpoint" },
];

function CreateJourneyModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [durationWeeks, setDurationWeeks] = useState(8);
  const [steps, setSteps] = useState([
    { order: 1, type: "capscan", title: "Initial CapScan", target_skill_code: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const addStep = () => {
    setSteps((p) => [...p, { order: p.length + 1, type: "coaching", title: "", target_skill_code: "" }]);
  };
  const removeStep = (idx) => {
    setSteps((p) => p.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  };
  const updateStep = (idx, patch) => {
    setSteps((p) => p.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const submit = async () => {
    setErr("");
    if (title.trim().length < 5) return setErr("Title needs at least 5 characters");
    if (steps.length < 1) return setErr("Add at least one step");
    const cleaned = steps.filter((s) => (s.title || "").trim().length > 0);
    if (cleaned.length < 1) return setErr("Give each step a title");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/journeys`, {
        title: title.trim(),
        goal: goal.trim(),
        duration_weeks: parseInt(durationWeeks || 8, 10),
        steps: cleaned,
      });
      onCreated(r.data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="create-journey-modal">
        <div className="bg-navy p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="font-playfair text-white text-xl font-bold">New Development Journey</h2>
            <p className="font-dm text-gold/80 text-xs">Plan a multi-step development path with CapScan, Coaching and Milestones</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="font-dm text-navy text-xs font-bold uppercase">Journey title</label>
            <input data-testid="journey-title-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. From IC to People Leader (12-week plan)" className="w-full bg-cream border border-navy/10 rounded-lg p-3 mt-1 font-dm text-sm text-navy" />
          </div>

          <div>
            <label className="font-dm text-navy text-xs font-bold uppercase">Outcome goal</label>
            <textarea data-testid="journey-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} rows={2} placeholder="What does success look like at the end?" className="w-full bg-cream border border-navy/10 rounded-lg p-3 mt-1 font-dm text-sm text-navy" />
          </div>

          <div>
            <label className="font-dm text-navy text-xs font-bold uppercase">Duration (weeks)</label>
            <input data-testid="journey-duration-input" type="number" min="1" max="52" value={durationWeeks} onChange={(e) => setDurationWeeks(e.target.value)} className="w-32 bg-cream border border-navy/10 rounded-lg p-2 mt-1 font-dm text-sm text-navy" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-dm text-navy text-xs font-bold uppercase">Steps</label>
              <button onClick={addStep} data-testid="journey-add-step-btn" className="text-gold text-xs font-bold flex items-center gap-1 hover:text-gold/80"><Plus className="w-3 h-3" /> Add step</button>
            </div>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="bg-cream border border-navy/10 rounded-lg p-3 flex flex-col gap-2" data-testid={`journey-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-dm text-navy/40 text-xs font-bold w-6">{i + 1}.</span>
                    <select value={s.type} onChange={(e) => updateStep(i, { type: e.target.value })} data-testid={`journey-step-type-${i}`} className="bg-white border border-navy/10 rounded-lg p-2 font-dm text-xs text-navy">
                      {STEP_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                    <input type="text" value={s.title} onChange={(e) => updateStep(i, { title: e.target.value })} placeholder="Step title" data-testid={`journey-step-title-${i}`} className="flex-1 bg-white border border-navy/10 rounded-lg p-2 font-dm text-xs text-navy" />
                    {steps.length > 1 && (
                      <button onClick={() => removeStep(i)} className="text-red-500 hover:text-red-700 p-1" data-testid={`journey-remove-step-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {err && <p className="text-red-600 text-xs font-dm" data-testid="journey-error">{err}</p>}

          <div className="flex gap-2 pt-2 border-t border-navy/10">
            <button onClick={submit} disabled={submitting} data-testid="journey-submit-btn" className="bg-gold text-navy font-dm font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-gold/90 disabled:opacity-60">
              {submitting ? "Creating…" : "Create journey"}
            </button>
            <button onClick={onClose} className="border border-navy/20 text-navy font-dm text-sm px-5 py-2.5 rounded-lg hover:bg-navy/5">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function JourneyDetailModal({ journey, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);

  const completeStep = async (order) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/api/journeys/${journey._id}/complete-step`, { step_order: order });
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const completed = (journey.steps || []).filter((s) => s.status === "completed").length;
  const total = (journey.steps || []).length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="journey-detail-modal">
        <div className="bg-navy p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex-1">
            <h2 className="font-playfair text-white text-xl font-bold">{journey.title}</h2>
            <p className="font-dm text-gold/80 text-xs mt-1">{journey.goal || "No goal set"}</p>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
            </div>
            <p className="font-dm text-white/60 text-[10px] mt-1">{completed} / {total} steps · {pct}%</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1.5 ml-3"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-3">
          {(journey.steps || []).map((s, i) => (
            <div key={i} className={`border rounded-lg p-4 flex items-center justify-between gap-3 ${s.status === "completed" ? "bg-green-50 border-green-200" : "bg-cream border-navy/10"}`} data-testid={`journey-detail-step-${i}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-dm text-navy/40 text-xs font-bold">Step {s.order}</span>
                  <span className="bg-gold/15 text-navy font-dm text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">{s.type}</span>
                  {s.status === "completed" && <span className="bg-green-200 text-green-800 font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">Done</span>}
                </div>
                <p className="font-dm text-navy text-sm font-semibold">{s.title}</p>
                {s.completed_at && <p className="font-dm text-navy/40 text-[10px] mt-1">Completed {new Date(s.completed_at).toLocaleDateString("en-IN")}</p>}
              </div>
              {s.status !== "completed" && (
                <button onClick={() => completeStep(s.order)} disabled={busy} data-testid={`journey-complete-step-${s.order}`} className="bg-navy text-gold font-dm font-bold text-xs px-3 py-2 rounded-lg disabled:opacity-60 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Mark done
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function JourneysTab() {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/journeys/mine`);
      setJourneys(r.data || []);
    } catch {
      setJourneys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refreshSelected = async () => {
    if (!selected) return;
    try {
      const r = await axios.get(`${API}/api/journeys/${selected._id}`);
      setSelected(r.data);
      load();
    } catch { /* */ }
  };

  const deleteJourney = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this journey?")) return;
    try {
      await axios.delete(`${API}/api/journeys/${id}`);
      setJourneys((p) => p.filter((j) => j._id !== id));
    } catch { alert("Failed to delete"); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6" data-testid="my-journeys-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-playfair text-navy text-xl font-semibold flex items-center gap-2">
          <Compass className="w-5 h-5 text-gold" /> My Journeys ({journeys.length})
        </h2>
        <button onClick={() => setShowCreate(true)} data-testid="new-journey-btn" className="bg-gold text-navy font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold/90">
          <Plus className="w-4 h-4" /> New journey
        </button>
      </div>

      {loading ? (
        <p className="font-dm text-navy/40 text-sm">Loading journeys…</p>
      ) : journeys.length === 0 ? (
        <div className="text-center py-12" data-testid="journeys-empty">
          <Target className="w-10 h-10 text-gold mx-auto mb-3" />
          <p className="font-playfair text-navy text-lg font-bold mb-2">No journeys yet</p>
          <p className="font-dm text-navy/60 text-sm mb-4">Build a multi-step plan that stitches CapScan + coaching + milestones into one focused programme.</p>
          <button onClick={() => setShowCreate(true)} data-testid="journeys-empty-cta" className="bg-navy text-gold font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-navy/90">Create your first journey</button>
        </div>
      ) : (
        <div className="space-y-3">
          {journeys.map((j) => {
            const completed = (j.steps || []).filter((s) => s.status === "completed").length;
            const total = (j.steps || []).length;
            const pct = total ? Math.round((completed / total) * 100) : 0;
            return (
              <div key={j._id} onClick={() => setSelected(j)} className="border border-navy/10 rounded-lg p-4 hover:bg-cream/50 cursor-pointer transition-colors" data-testid={`journey-row-${j._id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-dm text-navy text-sm font-bold">{j.title}</p>
                    {j.goal && <p className="font-dm text-navy/60 text-xs mt-0.5">{j.goal}</p>}
                    <p className="font-dm text-navy/40 text-[10px] mt-1">{j.duration_weeks} weeks · {total} steps · {j.status}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={(e) => deleteJourney(j._id, e)} className="p-1.5 text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    <ChevronRight className="w-4 h-4 text-navy/40" />
                  </div>
                </div>
                <div className="h-1.5 bg-navy/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                </div>
                <p className="font-dm text-navy/40 text-[10px] mt-1">{completed} / {total} steps complete · {pct}%</p>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateJourneyModal onClose={() => setShowCreate(false)} onCreated={() => load()} />}
      {selected && <JourneyDetailModal journey={selected} onClose={() => setSelected(null)} onChanged={refreshSelected} />}
    </div>
  );
}
